import MenuRoundedIcon from "@mui/icons-material/MenuRounded";
import { ROLES } from "@/features/auth";
import {
  AppBar,
  IconButton,
  Slide,
  Toolbar,
  Typography,
  useScrollTrigger,
} from "@mui/material";
import { useTranslation } from "react-i18next";
import { useLocation } from "react-router-dom";
import { LanguageDropDown } from "./LanguageDropDown";
import { MenuDropDown } from "./MenuDropDown";
import { ModeToggleDropDown } from "./ModeToggleDropDown";
import { NotificationButton } from "../notifications/NotificationsButton";
import { useNotifications } from "@/components/notifications/api/getNotifications";
import { useStore } from "@/zustand/store";
import { useEffect } from "react";
import { useAuthorization } from "@/lib/authorization";
import { useAuth } from "@/providers/auth";

type HideOnScrollProps = {
  children: React.ReactElement;
};

const HideOnScroll = ({ children }: HideOnScrollProps) => {
  const trigger = useScrollTrigger();

  return (
    <Slide appear={false} direction="down" in={!trigger}>
      {children}
    </Slide>
  );
};

type HeaderProps = {
  onDrawerToggle: () => void;
};

export const Header = (props: HeaderProps) => {
  const { onDrawerToggle } = props;

  const { pathname } = useLocation();
  const { user } = useAuth();
  const { checkRoles } = useAuthorization();

  const { t, i18n } = useTranslation();

  const routeName = pathname.substring(1).split("/")[0];
  const titleKey =
    routeName === "agents" && checkRoles([ROLES.EMPLOYEE])
      ? "navigation.hunters"
      : "navigation." + routeName;

  const { data: notifications } = useNotifications({ userId: user?.userId });
  const setNotifications = useStore((state) => state.setNotifications);

  useEffect(() => {
    if (notifications) {
      setNotifications(notifications);
    }
  }, [notifications, setNotifications]);

  return (
    <>
      <HideOnScroll>
        <AppBar
          position="sticky"
          color="primary"
          elevation={0}
          sx={{
            py: 1,
            borderBottom: 1,
            borderColor: "divider",
          }}
        >
          <Toolbar>
            <IconButton
              color="inherit"
              aria-label="open drawer"
              onClick={onDrawerToggle}
              edge="start"
              size="medium"
            >
              <MenuRoundedIcon />
            </IconButton>
            <Typography
              fontSize={{ xs: 22, sm: 24 }}
              component="h1"
              flexGrow={1}
            >
              {i18n.exists(titleKey) ? t(titleKey) : ""}
            </Typography>
            <NotificationButton />
            <ModeToggleDropDown />
            <LanguageDropDown />
            <MenuDropDown />
          </Toolbar>
        </AppBar>
      </HideOnScroll>
    </>
  );
};
